import { action, computed, makeObservable, observable, runInAction } from "mobx";
import type { MutableRefObject, RefObject } from "react";
import type { Direction } from "../../../common/utils/todo-utils";
import { WebEvents } from "@web/src/app/web-events";
import type { GroupKey, TodoId } from "@common/types/todo";
import type { HighlightableId } from "@web/src/app/use-cases/todo-highlight";
import type { Rect } from "@web/src/app/utils/rect";
import { withCenter } from "@web/src/app/utils/rect";

export type HighlightObject = TodoHighlightableObject | AddTodoButtonHighlightableObject;

type HighlightObjectBase = {
  id: HighlightableId;
  ref: MutableRefObject<HTMLElement | null | undefined>;
  onEnter: () => void;
};

export type TodoHighlightableObject = HighlightObjectBase & {
  kind: "todo";
  data: {
    textareaRef: RefObject<HTMLTextAreaElement | null>;
    /**
     * The actual todoId, whereas the highlightable id includes the group.
     * A single todo id could present itself as distinct highlightables
     */
    todoId: TodoId;
    groupKey: GroupKey;
  };
};

export type AddTodoButtonHighlightableObject = HighlightObjectBase & {
  kind: "add-todo-button";
  data: {
    groupKey: GroupKey;
  };
};

type HighlightStrategy = "outBoundCenter" | "axisOverlapCenter" | "visualGroupVertical";

export default class HighlightStore {
  @observable
  currentHighlightId: HighlightableId | null = null;
  containerRef: HTMLDivElement | null = null;

  @computed
  get currentHighlightObj() {
    return this.currentHighlightId ? (this.highlightObjMap.get(this.currentHighlightId) ?? null) : null;
  }

  @computed
  get currentRef() {
    return this.currentHighlightId ? (this.highlightObjMap.get(this.currentHighlightId)?.ref.current ?? null) : null;
  }

  /**
   * Populated by useHighlightable - maintains a reference
   * to all highlightable objects
   */
  highlightObjMap = observable.map<HighlightableId, HighlightObject>([], { deep: false });

  constructor() {
    makeObservable(this);
  }

  setContainerRef(ref: HTMLDivElement | null) {
    this.containerRef = ref;
  }

  @computed
  get containerRect() {
    if (!this.containerRef) return new DOMRect();
    const containerRect = this.containerRef.getBoundingClientRect();
    return new DOMRect(containerRect.x, containerRect.y, this.containerRef.scrollWidth, this.containerRef.scrollHeight);
  }

  getHighlightObjInDirectionFromRect(
    direction: Direction | "center",
    _referenceRect?: DOMRect | undefined,
    strategy: HighlightStrategy = direction === "up" || direction === "down" ? "axisOverlapCenter" : "outBoundCenter",
    wrappable: boolean = true,
  ): HighlightObject | null {
    const referenceRect = withCenter(
      _referenceRect ??
        this.currentRef?.getBoundingClientRect() ??
        new DOMRect(screen.width / 2, screen.height / 2, 0, 0),
    );
    const highlightObjs = Array.from([...this.highlightObjMap.values()]);

    if (direction === "center") {
      const candidates = highlightObjs.filter((obj) => referenceRectWithinItemFilter(obj.ref.current, referenceRect));
      return candidates[0] ?? null;
    }

    if (strategy === "outBoundCenter") {
      // filter those in a direction
      const candidates = highlightObjs.filter((obj) => {
        if (obj.ref.current === this.currentRef) return false; // cannot select itself
        return outBoundCenterFilter(obj.ref.current, referenceRect, direction);
      });

      // if we can't find, we start from a wrapped point and try again
      if (!candidates.length && wrappable) {
        // find one on the opposite side

        switch (direction) {
          case "up":
            return this.getHighlightObjInDirectionFromRect(
              direction,
              new DOMRect(referenceRect.centerX, this.containerRect.bottom, 0, 0),
              strategy,
              false,
            );
          case "down":
            return this.getHighlightObjInDirectionFromRect(
              direction,
              new DOMRect(referenceRect.centerX, this.containerRect.top, 0, 0),
              strategy,
              false,
            );
          case "left":
            return this.getHighlightObjInDirectionFromRect(
              direction,
              new DOMRect(this.containerRect.right, referenceRect.centerY, 0, 0),
              strategy,
              false,
            );
          case "right":
            return this.getHighlightObjInDirectionFromRect(
              direction,
              new DOMRect(this.containerRect.left, referenceRect.centerY, 0, 0),
              strategy,
              false,
            );
        }
      }

      // find the closest one
      let closest: HighlightObject | null = null;
      let closestDistance = Infinity;

      // If we are looking left and right, we want to prioritise the
      // vertical visual grouping (and vice versa for up and down)
      const AXIS_WEIGHT_FACTORS =
        direction === "left" || direction === "right"
          ? {
              VERTICAL: 50,
              HORIZONTAL: 100,
            }
          : {
              VERTICAL: 100,
              HORIZONTAL: 50,
            };

      for (const candidate of candidates) {
        const _rect = candidate.ref.current?.getBoundingClientRect();
        if (!_rect) continue;
        const rect = withCenter(_rect);
        const distance =
          AXIS_WEIGHT_FACTORS.VERTICAL * Math.abs(rect.centerY - referenceRect.centerY) +
          AXIS_WEIGHT_FACTORS.HORIZONTAL * Math.abs(rect.centerX - referenceRect.centerX);
        if (distance < closestDistance) {
          closest = candidate;
          closestDistance = distance;
        }
      }

      return closest ?? this.currentHighlightObj;
    }

    if (strategy === "axisOverlapCenter") {
      // filter those in a row or column
      // sort it by x/y, then segment it by direction
      // if we can't find, we pick one from the opposite side
      const candidates = highlightObjs.filter((obj) => {
        if (!obj.ref.current) return false;
        if (obj.ref.current === this.currentRef) return false; // cannot select itself
        return axisOverlapCenterFilter(obj.ref.current, referenceRect, direction);
      });

      // IMPORTANT: sort in such a way that going down the list
      // is going in the direction we want
      candidates.sort((a, b) => {
        const rectA = withCenter(a.ref.current!.getBoundingClientRect());
        const rectB = withCenter(b.ref.current!.getBoundingClientRect());
        if (!rectA || !rectB) return 0;
        switch (direction) {
          case "up":
            return rectB.centerY - rectA.centerY; // traverse in reverse order
          case "down":
            return rectA.centerY - rectB.centerY;
          case "left":
            return rectB.centerX - rectA.centerX; // traverse in reverse order
          case "right":
            return rectA.centerX - rectB.centerX;
        }
      });

      const traverseOrder = direction === "up" || direction === "left" ? "reverse" : "normal";
      const property = direction === "up" || direction === "down" ? "centerY" : "centerX";

      const findFirstInDirectionIdx = (candidates: HighlightObject[]) => {
        for (let i = 0; i < candidates.length; i++) {
          const candidate = candidates[i]!;
          const rect = withCenter(candidate.ref.current!.getBoundingClientRect());
          if (
            (traverseOrder === "normal" && rect[property] <= referenceRect[property]) ||
            (traverseOrder === "reverse" && rect[property] >= referenceRect[property])
          ) {
            continue;
          }
          return i;
        }
        return null;
      };

      const i = findFirstInDirectionIdx(candidates);

      // if can't find one, we wrap around
      if (i === null) {
        if (wrappable) return candidates[0] ?? this.currentHighlightObj;
        // if not wrapping, return first in the opposite direction
        return candidates[candidates.length - 1] ?? this.currentHighlightObj;
      }
      return candidates[i]! ?? this.currentHighlightObj;
    }

    return this.currentHighlightObj;
  }

  getHighlightObjInDirectionFromId(
    direction: Direction,
    fromHighlightableId: HighlightableId,
    strategy: HighlightStrategy,
    wrappable: boolean = true,
  ) {
    const highlightable = this.highlightObjMap.get(fromHighlightableId || this.currentHighlightId || "") ?? null;
    const referenceRect =
      highlightable?.ref.current?.getBoundingClientRect() ?? new DOMRect(screen.width / 2, screen.height / 2, 0, 0);
    return this.getHighlightObjInDirectionFromRect(direction, referenceRect, strategy, wrappable);
  }

  onHighlightableDeregister(id: HighlightableId, el: HTMLElement | null | undefined) {
    // IMPORTANT: el is quickly fading
    const referenceRect = el?.getBoundingClientRect() ?? new DOMRect(screen.width / 2, screen.height / 2, 0, 0);
    const selectNext = () => {
      const target =
        this.getHighlightObjInDirectionFromRect("up", referenceRect, "axisOverlapCenter", false) ??
        this.getHighlightObjInDirectionFromRect("down", referenceRect, "axisOverlapCenter", false) ??
        this.getHighlightObjInDirectionFromRect("left", referenceRect, "outBoundCenter", false) ??
        this.getHighlightObjInDirectionFromRect("right", referenceRect, "outBoundCenter", false);

      runInAction(() => this.setCurrentHighlightId(target?.id));
    };

    if (this.currentHighlightId === id) {
      // check after persistent duration whether a new highlightable has been registered
      // if not, then select the next one
      setTimeout(() => {
        if (!this.highlightObjMap.has(id)) {
          selectNext();
        }
      }, 30);
    }
  }

  @action
  setCurrentHighlightId(_id: HighlightableId | null | undefined) {
    // const obj = _id ? this.highlightObjMap.get(_id) : null;
    // const id = _id ? (obj ? _id : null) : null;

    // Instead of validating like the above logic, we leave it so that highlightables can
    // persist between frames, where new renders might not have subscribed yet
    const id = _id ?? null;
    this.currentHighlightId = id;

    this.scrollCurrentIntoView();

    id && WebEvents.emit("WEB_ON_HIGHLIGHT_CHANGED", id);
  }

  scrollCurrentIntoView() {
    const obj = this.currentHighlightObj;
    if (obj?.ref.current) {
      let rect = obj.ref.current.getBoundingClientRect();
      let headerHeight = 0;
      let footerHeight = 0;
      if (
        rect.bottom < headerHeight ||
        rect.top > window.innerHeight - footerHeight ||
        rect.left > window.innerWidth ||
        rect.right < 0
      ) {
        obj.ref.current && obj.ref.current.scrollIntoView({ block: "center", inline: "nearest" });
      }
    }
  }
}

// Strategy 1: out bound center (generous)
// algo: center of target should be outside of referenceRect's bounds
const outBoundCenterFilter = (item: HTMLElement | null | undefined, referenceRect: Rect, direction: Direction) => {
  const threshold = 1;
  if (!item) return false;
  const eachRect = withCenter(item.getBoundingClientRect());

  switch (direction) {
    case "up":
      return eachRect.centerY < referenceRect.top - threshold;
    case "down":
      return eachRect.centerY > referenceRect.bottom + threshold;
    case "left":
      return eachRect.centerX < referenceRect.left - threshold;
    case "right":
      return eachRect.centerX > referenceRect.right + threshold;
  }
};

// Strategy 2: in bound center (strict)
// algo: center of target overlay with column/row based on referenceRect's bounds
const axisOverlapCenterFilter = (item: HTMLElement | null | undefined, referenceRect: Rect, direction: Direction) => {
  const threshold = 1;
  if (!item) return false;
  const eachRect = withCenter(item.getBoundingClientRect());

  switch (direction) {
    case "up":
    case "down":
      return eachRect.centerX < referenceRect.right + threshold && eachRect.centerX > referenceRect.left - threshold;
    case "left":
    case "right":
      return eachRect.centerY < referenceRect.bottom + threshold && eachRect.centerY > referenceRect.top - threshold;
  }
};

const referenceRectWithinItemFilter = (item: HTMLElement | null | undefined, referenceRect: Rect) => {
  if (!item) return false;
  const eachRect = withCenter(item.getBoundingClientRect());
  return (
    referenceRect.centerX <= eachRect.right &&
    referenceRect.centerX >= eachRect.left &&
    referenceRect.centerY <= eachRect.bottom &&
    referenceRect.centerY >= eachRect.top
  );
};
